import { createContext, useContext, useState, ReactNode, useRef, useEffect, useMemo } from 'react';
import { ContentType, EntryType, SectionType, SectionTypes, TemplateStyling, ezyVetMappingToObject, checkForDuplicateNames, communicationTemplateTones, saveInstinctMapping, IntegrationType, instinctMappingToObject, isVetRecOwner, normalizeSectionOrEntryName, VetspireMapping, VetspireTypes, TemplatePermissions, getTemplateTypeDefaultKey } from './TemplateUtils';
import { TemplateType } from '@neum/frontend-shared/constants/templateData';
import { v4 as uuidv4 } from 'uuid';
import { useSupabase } from '../../supabase/SupabaseContext';
import { useUser } from '@clerk/clerk-react';
import { createTemplate, fetchSessionById, fetchTempalteById, generateSoapNotes, postDefaultDischargeTemplate, postDefaultRecordsRecapTemplate, postDefaultTemplate, updateLockTemplate, updateTemplate } from '../../ServerActions';
import { useAuth } from '@clerk/clerk-react';
import { advancedPlan, advancedSubjective, physical_exam, simpleAssessment } from './SectionDefinitions';
import { useLocation } from "react-router-dom"
import { UserAction } from '../../utils/vercelAnaytics';
import { GetTemplateDefinition, GetTemplateSignedURL } from '../../supabase/supabaseProxy'
import { executeWithRetries } from '@neum/frontend-shared/utils/retryUtils';
import { useVetRec } from '../../providers/VetRecProvider';
import { useNavigate } from 'react-router-dom';
import { SessionObject } from '../../utils/SessionUtils';
import { objectToJsonFile } from '../../utils/UploadUtils';
import { EzyVetCategories, EzyVetSection, EzyVetVitalsCategories, naiveMappingAttempt, naiveVitalsMappingAttempt, saveEzyVetMapping } from '@neum/frontend-shared/utils/ezyVetUtils';
import { createDocumentSpecification } from '@neum/frontend-shared/utils/metagraphUtils';
import { processDocuments } from '../../serverActions/sessionMetagraphApis';

const initTemplate = [ 
    advancedSubjective,
    physical_exam,
    simpleAssessment,
    advancedPlan,
]

interface DeletedItem {
  type: 'section' | 'entry'
  sectionKey: string
  section?: SectionType
  entryKey?: string
  parentEntryKey?: string
  entry?: EntryType
  index?: number
}

// Define the shape of your context state here
interface TemplateContextType {
  name: string|undefined;
  items: SectionType[];
  mapping: {[key:string]:EzyVetSection}
  styling: TemplateStyling
  integration: string | undefined
  entryHoverIndex: string | undefined
  sectionHoverIndex: string | undefined
  subSectionHover: boolean
  subEntryHoverIndex: string | undefined
  sectionDragged: boolean | undefined
  entryDragged: boolean | undefined
  templateType: TemplateType
  templateTone: string | undefined
  templateLoading: boolean
  templateId: string
  duplicatedFromTemplateId: string | undefined
  vetspireMapping: VetspireMapping
  existingTemplate: boolean
  showReprocessNotesModal: boolean
  sessionToUpdate: SessionObject | undefined
  isSuperOrgTemplate: boolean
  shouldApplySuperOrgPermissions: boolean
  duplicate:boolean
  editLockedToOwnerLoading:boolean
  editLockedToOwner:boolean
  isTemplateOwner: boolean
  templateOwner: string | undefined
  isPersonalTemplate: boolean
  templateCreateEnabledPermission: boolean
  templateEditEnabledPermission: boolean
  verboseMode: boolean
  onEditLockedToOwnerChanged: (locked:boolean) => Promise<void>
  addEntryToItem: (newEntry: EntryType) => void
  updateSectionName: (sectionKey: string, newName: string) => void
  updateSectionType: (sectionKey: string, type: SectionTypes) => void
  updateSectionEmptyName: (sectionKey: string, emptyName: boolean) => void
  updateSectionDescription: (sectionKey: string, newDescription: string) => void
  updateSectionDefaultValue: (sectionKey: string, newDefaultValue: string) => void
  updateSectionHardcodedValue: (sectionKey: string, hardcodedValue: string) => void
  updateSectionEmptyHardcodedValue: (sectionKey: string, emptyHardcodedValue: boolean) => void
  addSection: (newSection: SectionType, insertAtStart?: boolean) => void
  updateName: (name:string) => void
  deleteSection: (sectionKey:string) => void
  updateEntryName: (sectionKey: string, entryKey: string, newName: string, parentEntryKey?: string) => void
  updateEntryEmptyName: (sectionKey: string, entryKey: string, allowEmptyName: boolean, parentEntryKey?: string) => void
  updateEntryDescription: (sectionKey: string, entryKey: string, newDescription?: string, parentEntryKey?: string) => void
  updateEntryHardcodedValue: (sectionKey: string, entryKey: string, hardcodedValue: string | undefined, parentEntryKey?: string) => void
  updateEntryEmptyHardcodedValue: (sectionKey: string, entryKey: string, allowEmptyHardcodedValue: boolean, parentEntryKey?: string) => void
  updateEntryType: (sectionKey: string, entryKey: string, newType: ContentType, parentEntryKey?:string) => void
  updateEntryDefaultValue: (sectionKey: string, entryKey: string, newDefaultValue: string | undefined, parentEntryKey?: string) => void
  deleteEntry: (sectionKey: string, entryKey: string, parentEntryKey?: string) => void
  swapSectionsByKeys: (key1: string, key2: string) => void
  swapEntries:(sectionKey: string, entryKey1: string, entryKey2: string) => void
  swapSubEntries:(sectionKey: string, parentEntryKey: string, subEntryKey1: string, subEntryKey2: string) => void
  saveTemplate: () => Promise<void>
  validateTemplate: () => [string, boolean]
  overrideTemplate: (array_sections: { [key: string]: any }[]) => void
  updateEzyvetSectionName: (key: string, newName: string) => void
  updateEzyVetSectionCategory: (key: string, newCategory: EzyVetCategories) => void
  updateEzyVetVitalCategory: (sectionKey: string, entryKey:string, newCategory: EzyVetVitalsCategories)  => void
  saveMapping: () => Promise<void>
  updateIntegration: (integration:string) => void
  saveStyling: (styling:TemplateStyling) => Promise<void>
  updateStyling: (styling:TemplateStyling) => void
  handleSectionOver: (index:string|undefined) => void
  handleEntryOver: (index:string|undefined) => void
  handleSubSectionOver: (index:boolean) => void
  handleSubEntryOver: (index:string|undefined) => void
  handleSectionDragged: (index:boolean) => void
  handleEntryDragged: (index:boolean) => void
  updateTemplateType: (type:TemplateType) => void
  handleTemplateTone: (tone:string) => void
  updateVetspireType: (type:VetspireTypes, id?:string) => void
  handleReprocessNotes: (reprocess:boolean) => Promise<void>
  hasPermission: (...permissions: TemplatePermissions[]) => boolean
  undoMessage: string | undefined
  dismissUndo: () => void
  handleUndo: () => void
  updateVerboseMode: (verboseMode:boolean) => void
  isTemplateDefaultedInClerk: boolean
  setDefaultTemplate: (templateId:string, templateType:TemplateType) => Promise<void>
}

const TemplateContext = createContext<TemplateContextType>({
  name: undefined,
  items: initTemplate, 
  mapping: {},
  styling: {
    title_bold:true,
    title_italic: false,
    abnormals_bolded: false,
    show_not_mentioned: true,
    bullet_points: true,
    list_bullet_points: true,
    abnormals_color: false,
    abnormals_uppercase: false
  },
  integration: undefined,
  entryHoverIndex: undefined,
  sectionHoverIndex: undefined,
  subSectionHover: false,
  subEntryHoverIndex: undefined,
  sectionDragged: undefined,
  entryDragged: undefined,
  templateType: TemplateType.MEDICAL,
  templateTone: communicationTemplateTones[0].value,
  templateLoading: true,
  templateId: uuidv4(),
  duplicatedFromTemplateId: undefined,
  vetspireMapping: {type:VetspireTypes.ENCOUNTER},
  existingTemplate: false,
  showReprocessNotesModal: false,
  sessionToUpdate: undefined,
  isSuperOrgTemplate: false,
  shouldApplySuperOrgPermissions: false,
  duplicate: false,
  editLockedToOwnerLoading: false,
  editLockedToOwner: false,
  isTemplateOwner: false,
  templateOwner: undefined,
  isPersonalTemplate: false,
  templateCreateEnabledPermission: false,
  templateEditEnabledPermission: false,
  verboseMode: false,
  onEditLockedToOwnerChanged: () => {throw new Error('onEditLockedToOwnerChanged not implemented')},
  addEntryToItem: () => { throw new Error('addEntryToItem not implemented'); },
  updateSectionName: () => { throw new Error('updateSectionName not implemented'); },
  updateSectionEmptyName: () => { throw new Error('updateSectionEmptyName not implemented'); },
  updateSectionType: () => { throw new Error('updateSectionType not implemented'); },
  updateSectionDescription: () => { throw new Error('updateSectionDescription not implemented'); },
  updateSectionDefaultValue: () => { throw new Error('updateSectionDefaultValue not implemented'); },
  updateSectionHardcodedValue: () => { throw new Error('updateSectionHardcodedValue not implemented'); },
  updateSectionEmptyHardcodedValue: () => { throw new Error('updateSectionEmptyHardcodedValue not implemented'); },
  addSection: () => {throw new Error('addSection not implemented');},
  updateName: () => {throw new Error('updateName not implemented')},
  deleteSection: () => {throw new Error('deleteSection not implemented')},
  updateEntryName: () => {throw new Error('updateEntryName not implemented')},
  updateEntryEmptyName: () => {throw new Error('updateEntryEmptyName not implemented')},
  updateEntryDescription: () => {throw new Error('updateEntryDescription not implemented')},
  updateEntryHardcodedValue: () => {throw new Error('updateEntryHardcodedValue not implemented')},
  updateEntryEmptyHardcodedValue: () => {throw new Error('updateEntryEmptyHardcodedValue not implemented')},
  updateEntryDefaultValue: () => {throw new Error('updateEntryDefaultValue not implemented')},
  updateEntryType: () => {throw new Error('updateEntryType not implemented')},
  deleteEntry: () => {throw new Error('deleteEntry not implemented')},
  swapSectionsByKeys: () => {throw new Error('swapSectionsByKeys not implemented')},
  swapEntries: () => {throw new Error('swapEntries not implemented')},
  swapSubEntries: () => {throw new Error('swapSubEntries not implemented')},
  saveTemplate: () => {throw new Error('saveTemplate not implemented')},
  validateTemplate: () => ["An error ocurred" , false],
  overrideTemplate: () => {throw new Error('overrideTemplate not implemented')},
  updateEzyvetSectionName: () => {throw new Error('updateEzyvetSectionName not implemented')},
  updateEzyVetSectionCategory: () => {throw new Error('updateEzyVetSectionCategory not implemented')},
  updateEzyVetVitalCategory: () => {throw new Error('updateEzyVetVitalCategory not implemented')},
  saveMapping: () => {throw new Error('saveMapping not implemented')},
  updateIntegration: () => {throw new Error('updateIntegration not implemented')},
  saveStyling: () => {throw new Error('saveStyling not implemented')},
  updateStyling: () => {throw new Error('saveStyling not implemented')},
  handleSectionOver: () => {throw new Error('handleSectionOver not implemented')},
  handleEntryOver: () => {throw new Error('handleEntryOver not implemented')},
  handleSubSectionOver: () => {throw new Error('handleSubSectionOver not implemented')},
  handleSubEntryOver: () => {throw new Error('handleSubEntryOver not implemented')},
  handleSectionDragged: () => {throw new Error('handleSectionDragged not implemented')},
  handleEntryDragged: () => {throw new Error('handleEntryDragged not implemented')},
  updateTemplateType: () => {throw new Error('updateTemplateType not implemented')},
  handleTemplateTone: () => {throw new Error('handleTemplateTone not implemented')},
  updateVetspireType: () => {throw new Error('updateVetspireType not implemented')},
  handleReprocessNotes: () => {throw new Error('handleReprocessNotes not implemented')},
  hasPermission: () => {throw new Error('hasPermission not implemented')},
  undoMessage: undefined,
  dismissUndo: () => { throw new Error('dismissUndo not implemented') },
  handleUndo: () => { throw new Error('handleUndo not implemented') },
  updateVerboseMode: () => { throw new Error('updateVerboseMode not implemented') },
  isTemplateDefaultedInClerk: false,  
  setDefaultTemplate: () => {throw new Error('setDefaultTemplate not implemented')}
});

export const useTemplate = () => useContext(TemplateContext);

// Context provider component
export const TemplateProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser()
  const {getToken, orgId} = useAuth()
  const {uploadToSupabaseSignedURL} = useSupabase()
  const { pims, defaultVetspireBehavior, disableExportConfiguration, superOrg, superOrgTemplatePermissions, superOrgOwner, createEnabled, editEnabled, organizationAdmin, inOrganization, userSpecialty } = useVetRec()

  const [saved, setSaved] = useState<boolean>(false)
  const [items, setItems] = useState<SectionType[]>(initTemplate)
  const [integration, setIntegration] = useState<string>()
  const [mapping, setMapping] = useState<{[key:string]:EzyVetSection}>({})
  const [vetspireMapping, setVetspireMapping] = useState<VetspireMapping>({type: (Object.values(VetspireTypes).find((enumValue) => enumValue === defaultVetspireBehavior) ?? VetspireTypes.ENCOUNTER)})
  const [styling, setStyling] = useState<TemplateStyling>({
    title_bold:true,
    abnormals_bolded: false,
    show_not_mentioned: true,
    bullet_points: true,
    list_bullet_points: true,
    abnormals_color: false,
    abnormals_uppercase: false,
    title_italic: false
  })
  const [name, setName] = useState<string|undefined>(undefined)
  const [existingTemplate, setExistingTemplate] = useState<boolean>(false)
  const [templateId, setTemplateId] = useState<string>(uuidv4())
  const [duplicatedFromTemplateId, setDuplicatedFromTemplateId] = useState<string | undefined>(undefined)
  const [templateType, setTemplateType] = useState<TemplateType>(TemplateType.MEDICAL)
  const updateName = (name:string) => {setName(name)}
  const [templateOrganization, setTemplateOrganization] = useState<string>()
  const [sectionHoverIndex, setSectionHoverIndex] = useState<string>()
  const [entryHoverIndex, setEntryHoverIndex] = useState<string>()
  const [subSectionHover, setSubSectionHover] = useState<boolean>(false)
  const [subEntryHoverIndex, setSubEntryHoverIndex] = useState<string>()
  const [sectionDragged, setSectionDragged] = useState<boolean>()
  const [entryDragged, setEntryDragged] = useState<boolean>()
  const [templateTone, setTemplateTone] = useState<string>(communicationTemplateTones[1].value)
  const [templateLoading, setTemplateLoading] = useState<boolean>(false)
  const [promptToReprocessSession, setPromptToReprocessSession] = useState<boolean>(false)
  const [sessionToUpdate, setSessionToUpdate] = useState<SessionObject>()
  const [nodeIdToUpdate, setNodeIdToUpdate] = useState<string | undefined>()
  const [showReprocessNotesModal, setShowReprocessNotesModal] = useState<boolean>(false)
  const [isSuperOrgTemplate, setIsSuperOrgTemplate] = useState<boolean>(false)
  const [shouldApplySuperOrgPermissions, setShouldApplySuperOrgPermissions] = useState<boolean>(false)
  const [duplicate, setDuplicate] = useState<boolean>(false)
  const [editLockedToOwner, setEditLockedToOwner] = useState<boolean>(false)
  const [editLockedToOwnerLoading, setEditLockedToOwnerLoading] = useState<boolean>(false)
  const [isTemplateOwner, setIsTemplateOwner] = useState<boolean>(false)
  const [templateOwner, setTemplateOwner] = useState<string | undefined>()
  const [isPersonalTemplate, setIsPersonalTemplate] = useState<boolean>(false)
  const sectionHoverIndexRef = useRef(sectionHoverIndex)
  const entryHoverIndexRef = useRef(entryHoverIndex)
  const subSectionHoverRef = useRef(subSectionHover)
  const subEntryHoverIndexRef = useRef(subEntryHoverIndex)
  const navigate = useNavigate();
  const [lastDeletedItem, setLastDeletedItem] = useState<DeletedItem | undefined>()
  const [undoMessage, setUndoMessage] = useState<string | undefined>()
  const [verboseMode, setVerboseMode] = useState<boolean>(false)
  const isTemplateDefaultedInClerk = useMemo<boolean>(() => {
    return user?.publicMetadata[getTemplateTypeDefaultKey(templateType)] === "dynamic_" + templateId
  }, [user, templateType, templateId])
  

  const templateCreateEnabledPermission = useMemo<boolean>(() => (createEnabled || organizationAdmin),[createEnabled, organizationAdmin])
  const templateEditEnabledPermission = useMemo<boolean>(() => (editEnabled || organizationAdmin),[editEnabled, organizationAdmin])

  async function onEditLockedToOwnerChanged(locked: boolean) {
    setEditLockedToOwnerLoading(true)
    
    try {
      if (existingTemplate) {
        const token = await getToken({template:"supabase"}) ?? ""
        await updateLockTemplate(token, `dynamic_${templateId}`, locked)        
      }

      setEditLockedToOwner(locked)  
    } finally {
      setEditLockedToOwnerLoading(false)
    }
  }
  
  useEffect(() => {
    sectionHoverIndexRef.current = sectionHoverIndex
  }, [sectionHoverIndex])

  useEffect(() => {
    entryHoverIndexRef.current = entryHoverIndex
  }, [entryHoverIndex])

  useEffect(() => {
    subSectionHoverRef.current = subSectionHover
  }, [subSectionHover])

  useEffect(() => {
    subEntryHoverIndexRef.current = subEntryHoverIndex
  }, [subEntryHoverIndex])

  const handleSectionOver = (index:string|undefined) => {
    setSectionHoverIndex(index)
  }

  const handleEntryOver = (index:string|undefined) => {
    setEntryHoverIndex(index)
  }

  const handleSubSectionOver = (index:boolean) => {
    setSubSectionHover(index)
  }

  const handleSubEntryOver = (index:string|undefined) => {
    setSubEntryHoverIndex(index)
  }

  const handleSectionDragged = (index:boolean) => {
    setSectionDragged(index)
  }

  const handleEntryDragged = (index:boolean) => {
    setEntryDragged(index)
  }

  const handleTemplateTone = (tone:string) => {
    setTemplateTone(tone)
  }

  const addSection = (newSection: SectionType, insertAtStart: boolean = false) => {
    const sectionToAdd = { ...newSection, sectionKey: uuidv4() };
    if(sectionToAdd.type === SectionTypes.SPACES) {
      sectionToAdd.name = `vetrec_new_line_${sectionToAdd.sectionKey}`
    }
    setItems((currentItems) => insertAtStart ? [sectionToAdd, ...currentItems] : [...currentItems, sectionToAdd]);
    
    if(sectionHoverIndexRef.current){
      swapSectionsByKeys(sectionHoverIndexRef.current, sectionToAdd.sectionKey)
    }
    handleEntryOver(undefined)
    handleSectionOver(undefined)
    handleSubEntryOver(undefined)
    handleSubSectionOver(false)
  };

  const deleteSection = (sectionKey: string) => {
    setItems((currentItems) => {
      const sectionIndex = currentItems.findIndex(item => item.sectionKey === sectionKey);
      const deletedSection = currentItems[sectionIndex];
      setLastDeletedItem({
        type: 'section',
        sectionKey,
        section: deletedSection,
        index: sectionIndex
      });
      setUndoMessage(`Section "${deletedSection.name || 'Untitled'}" deleted`);
      return currentItems.filter(item => item.sectionKey !== sectionKey);
    });
  };

  const addEntryToItem = (newEntry: EntryType) => {
    if(subSectionHoverRef.current){
      addEntryToSubentries(newEntry)
    }
    else{
      addEntryToSection(newEntry)
    }
  };

  const addEntryToSection = (newEntry: EntryType) => {
    if(sectionHoverIndexRef.current === undefined){
      return
    }

    const entryToAdd = { ...newEntry, entryKey: uuidv4() };
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionHoverIndexRef.current
          ? { ...item, entries: [...item.entries, entryToAdd] }
          : item
      )
    );
    if(entryHoverIndexRef.current){
      swapEntries(sectionHoverIndexRef.current, entryHoverIndexRef.current, entryToAdd.entryKey)
    }

    handleEntryOver(undefined)
    handleSectionOver(undefined)
    handleSubEntryOver(undefined)
    handleSubSectionOver(false)
  };

  const addEntryToSubentries = (newSubEntry: EntryType) => {
    if(sectionHoverIndexRef.current === undefined || entryHoverIndexRef.current === undefined){
      return
    }

    const subEntryToAdd = { ...newSubEntry, entryKey: uuidv4() };
    setItems((currentItems) =>
        currentItems.map((section) =>
            section.sectionKey === sectionHoverIndexRef.current
                ? {
                    ...section,
                    entries: section.entries.map((entry) =>
                        entry.entryKey === entryHoverIndexRef.current
                            ? { ...entry, subEntries: [...(entry.subEntries || []), subEntryToAdd] }
                            : entry
                    )
                }
                : section
        )
    );

    if(subEntryHoverIndexRef.current){
      swapSubEntries(sectionHoverIndexRef.current, entryHoverIndexRef.current, subEntryHoverIndexRef.current, subEntryToAdd.entryKey)
    }
    handleEntryOver(undefined)
    handleSectionOver(undefined)
    handleSubEntryOver(undefined)
    handleSubSectionOver(false)
  };

  const updateSectionName = (sectionKey: string, newName: string) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionKey ? { ...item, name: newName } : item
      )
    );

    setMapping((currentMapping) => {
      const oldName = items.find(item => item.sectionKey === sectionKey)?.name?.toLowerCase();
      const updatedMapping = { ...currentMapping };
      if(oldName) {
        updatedMapping[newName.toLowerCase()] = updatedMapping[oldName];
        updatedMapping[newName.toLowerCase()].name = newName
        delete updatedMapping[oldName];
      }
      return updatedMapping;
    });
  };

  const updateSectionEmptyName = (sectionKey: string, emptyName: boolean) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionKey ? { ...item, emptyName: emptyName } : item
      )
    );
  };

  const updateSectionDescription = (sectionKey: string, newDescription: string) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionKey ? { ...item, description: newDescription } : item
      )
    );
  };

  const updateSectionDefaultValue = (sectionKey: string, newDefaultValue: string) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionKey ? { ...item, defaultValue: newDefaultValue } : item
      )
    );
  };

  const updateSectionHardcodedValue = (sectionKey: string, hardcodedValue: string) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionKey ? { ...item, hardcodedValue: hardcodedValue } : item
      )
    );
  };
  const updateSectionEmptyHardcodedValue = (sectionKey: string, emptyHardcodedValue: boolean) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionKey ? { ...item, emptyHardcodedValue: emptyHardcodedValue } : item
      )
    );
  };


  const updateSectionType = (sectionKey: string, type: SectionTypes) => {
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.sectionKey === sectionKey ? { ...item, type: type } : item
      )
    );
  };

  const updateEntryName = (sectionKey: string, entryKey: string, newName: string, parentEntryKey?: string) => {
    if(!parentEntryKey){
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === entryKey ? { ...entry, name: newName } : entry
                ),
              }
            : section
        )
      );
    }
    else {
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === parentEntryKey ? { ...entry, subEntries: entry.subEntries?.map((subEntry) => subEntry.entryKey === entryKey ? { ...subEntry, name: newName } : subEntry) } : entry
                ),
              }
            : section
        )
      );
    }
  };

  const updateEntryEmptyName = (sectionKey: string, entryKey: string, emptyName: boolean, parentEntryKey?: string) => {
    if(!parentEntryKey){
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey ? { ...section, entries: section.entries.map((entry) => entry.entryKey === entryKey ? { ...entry, emptyName: emptyName } : entry) } : section
        )
      );
    } 
    else {
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) => section.sectionKey === sectionKey ? { ...section, entries: section.entries.map((entry) => entry.entryKey === parentEntryKey ? { ...entry, subEntries: entry.subEntries?.map((subEntry) => subEntry.entryKey === entryKey ? { ...subEntry, emptyName: emptyName } : subEntry) } : entry) } : section)
      );
    }
  };

  const updateEntryDescription = (sectionKey: string, entryKey: string, newDescription: string | undefined, parentEntryKey?:string) => {
    if(!parentEntryKey){
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === entryKey ? { ...entry, description: newDescription } : entry
                ),
              }
            : section
        )
      );
    }
    else{
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === parentEntryKey ? { ...entry, subEntries: entry.subEntries?.map((subEntry) => subEntry.entryKey === entryKey ? { ...subEntry, description: newDescription } : subEntry) } : entry
                ),
              }
            : section
        )
      );
    }
  };

  const updateEntryHardcodedValue = (sectionKey: string, entryKey: string, hardcodedValue: string | undefined, parentEntryKey?:string) => {
    if(!parentEntryKey){
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === entryKey ? { ...entry, hardcodedValue: hardcodedValue } : entry
                ),
              }
            : section
        )
      );
    }
    else{
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === parentEntryKey ? { ...entry, subEntries: entry.subEntries?.map((subEntry) => subEntry.entryKey === entryKey ? { ...subEntry, hardcodedValue: hardcodedValue } : subEntry) } : entry
                ),
              }
            : section
        )
      );
    }
  };

  const updateEntryEmptyHardcodedValue = (sectionKey: string, entryKey: string, emptyHardcodedValue: boolean, parentEntryKey?:string) => {
    if(!parentEntryKey){
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) => section.sectionKey === sectionKey ? { ...section, entries: section.entries.map((entry) => entry.entryKey === entryKey ? { ...entry, emptyHardcodedValue: emptyHardcodedValue } : entry) } : section)
      );
    }
    else {
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) => section.sectionKey === sectionKey ? { ...section, entries: section.entries.map((entry) => entry.entryKey === parentEntryKey ? { ...entry, subEntries: entry.subEntries?.map((subEntry) => subEntry.entryKey === entryKey ? { ...subEntry, emptyHardcodedValue: emptyHardcodedValue } : subEntry) } : entry) } : section)
      );
    }
  };

  const updateEntryDefaultValue = (sectionKey: string, entryKey: string, newDefaultValue: string | undefined, parentEntryKey?: string) => {
    if(!parentEntryKey){
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === entryKey ? { ...entry, defaultValue: newDefaultValue } : entry
                ),
              }
            : section
        )
      );
    }
    else {
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === parentEntryKey ? { ...entry, subEntries: entry.subEntries?.map((subEntry) => subEntry.entryKey === entryKey ? { ...subEntry, defaultValue: newDefaultValue } : subEntry) } : entry
                ),
              }
            : section
        )
      );
    }
  };

  const updateEntryType = (sectionKey: string, entryKey: string, newType: ContentType, parentEntryKey?:string) => {
    if(!parentEntryKey){
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === entryKey ? { ...entry, type: newType } : entry
                ),
              }
            : section
        )
      );
    }
    else {
      setItems((currentItems: SectionType[]) =>
        currentItems.map((section) =>
          section.sectionKey === sectionKey
            ? {
                ...section,
                entries: section.entries.map((entry) =>
                  entry.entryKey === parentEntryKey ? { ...entry, subEntries: entry.subEntries?.map((subEntry) => subEntry.entryKey === entryKey ? { ...subEntry, type: newType } : subEntry) } : entry
                ),
              }
            : section
        )
      );
    }
  };

  const deleteEntry = (sectionKey: string, entryKey: string, parentEntryKey?: string) => {
    setItems((currentItems: SectionType[]) => {
      let deletedEntry: EntryType | undefined;
      let deletedIndex: number | undefined;
      
      if (!parentEntryKey) {
        const section = currentItems.find(s => s.sectionKey === sectionKey);
        deletedIndex = section?.entries.findIndex(e => e.entryKey === entryKey);
        deletedEntry = section?.entries[deletedIndex!];
      } else {
        const section = currentItems.find(s => s.sectionKey === sectionKey);
        const parentEntry = section?.entries.find(e => e.entryKey === parentEntryKey);
        deletedIndex = parentEntry?.subEntries?.findIndex(se => se.entryKey === entryKey);
        deletedEntry = parentEntry?.subEntries ? parentEntry.subEntries[deletedIndex!] : undefined;
      }
      
      if (deletedEntry !== undefined && deletedIndex !== undefined) {
        setLastDeletedItem({
          type: 'entry',
          sectionKey,
          entryKey,
          parentEntryKey,
          entry: deletedEntry,
          index: deletedIndex
        });
        setUndoMessage(`Entry "${deletedEntry.name || 'Untitled'}" deleted`);
      }
      
      // Remove the entry / sub-entry as before:
      return currentItems.map((section) => {
        if (section.sectionKey === sectionKey) {
          if (!parentEntryKey) {
            const updatedEntries = section.entries.filter((entry) => entry.entryKey !== entryKey);
            return { ...section, entries: updatedEntries };
          } else {
            const updatedEntries = section.entries.map((entry) => {
              if (entry.entryKey === parentEntryKey) {
                const updatedSubEntries = entry.subEntries?.filter((subEntry) => subEntry.entryKey !== entryKey);
                return { ...entry, subEntries: updatedSubEntries };
              }
              return entry;
            });
            return { ...section, entries: updatedEntries };
          }
        }
        return section;
      });
    });
  };

  const swapSectionsByKeys = (key1: string, key2: string) => {
    setItems((currentItems: SectionType[]) => {
      const index1 = currentItems.findIndex(section => section.sectionKey === key1);
      const index2 = currentItems.findIndex(section => section.sectionKey === key2);
  
      // Check if both sections are found, if not, return the current items without modification
      if (index1 === -1 || index2 === -1) return currentItems;
  
      // Initialize newItems at the beginning
      const newItems = [...currentItems];
  
      // Remove the item at index2 and insert it at index1
      const itemToMove = newItems.splice(index2, 1)[0];
      newItems.splice(index1, 0, itemToMove);
  
      // Correctly handle the shift-down operation
      if (index1 < index2) {
        // When moving from higher to lower, adjust the original item at index1 to move one position down
        const itemToShiftDown = newItems.splice(index1 + 1, 1)[0];
        newItems.splice(index1 + 1, 0, itemToShiftDown);
      } else {
        // When moving from lower to higher, no need to adjust as the other operations already manage the order
        // No additional code needed here
      }
  
      return newItems;
    });
  };

  const swapEntries = (sectionKey: string, entryKey1: string, entryKey2: string) => {
    setItems((currentItems: SectionType[]) =>
      currentItems.map((section) => {
        if (section.sectionKey === sectionKey) {
          const index1 = section.entries.findIndex(entry => entry.entryKey === entryKey1);
          const index2 = section.entries.findIndex(entry => entry.entryKey === entryKey2);
  
          // If either entry is not found, or they are the same, return the section unchanged
          if (index1 === -1 || index2 === -1 || index1 === index2) return section;
  
          const newEntries = [...section.entries];
  
          // Remove entry at index2 and insert it at index1
          const itemToMove = newEntries.splice(index2, 1)[0];
          newEntries.splice(index1, 0, itemToMove);
  
          if (index1 < index2) {
            // Adjust for when item is moved from a higher to a lower index
            const itemToShiftDown = newEntries.splice(index1 + 1, 1)[0];
            newEntries.splice(index1 + 1, 0, itemToShiftDown);
          } else {
            // Adjust for when item is moved from a lower to a higher index
            newEntries.splice(index1 + 1, 0, newEntries.splice(index1, 1)[0]);
          }
  
          // Return the section with updated entries
          return { ...section, entries: newEntries };
        }
        return section;
      })
    );
  };

  const swapSubEntries = (sectionKey: string, parentEntryKey: string, subEntryKey1: string, subEntryKey2: string) => {
      setItems((currentItems: SectionType[]) =>
          currentItems.map((section) => {
              if (section.sectionKey === sectionKey) {
                  // Find the index of the parent entry
                  const parentIndex = section.entries.findIndex(entry => entry.entryKey === parentEntryKey);

                  // If parent entry is not found, return the section unchanged
                  if (parentIndex === -1) return section;

                  const parentEntry = section.entries[parentIndex];
                  const subEntries = parentEntry.subEntries || [];

                  const subIndex1 = subEntries.findIndex(sub => sub.entryKey === subEntryKey1);
                  const subIndex2 = subEntries.findIndex(sub => sub.entryKey === subEntryKey2);

                  // If either subentry is not found, or they are the same, return the section unchanged
                  if (subIndex1 === -1 || subIndex2 === -1 || subIndex1 === subIndex2) return section;

                  // Swap the subentries
                  const newSubEntries = [...subEntries];
                  const temp = newSubEntries[subIndex1];
                  newSubEntries[subIndex1] = newSubEntries[subIndex2];
                  newSubEntries[subIndex2] = temp;

                  // Return the section with updated parent entry
                  return {
                      ...section,
                      entries: [
                          ...section.entries.slice(0, parentIndex),
                          { ...parentEntry, subEntries: newSubEntries },
                          ...section.entries.slice(parentIndex + 1)
                      ]
                  };
              }
              return section;
          })
      );
  };

  const updateTemplateType = (type:TemplateType) => {
    setTemplateType(type)
  }

  const saveTemplate = async () => {
    UserAction(user?.primaryEmailAddress?.emailAddress ?? "", "TemplateSaved")
    const file = objectToJsonFile(items, `${templateId}.json`, 'application/json')
    const temp_mapping = getTemplateMapping()

    if(existingTemplate){
      await executeWithRetries(async () => {
        const signedURL = await GetTemplateSignedURL(templateId, await getToken({template:"supabase"}) ?? '')
        await uploadToSupabaseSignedURL(file, signedURL)
      })

      await executeWithRetries(async () => {
        await updateTemplate(`dynamic_${templateId}`, name ?? "Template", await getToken({template:"supabase"}) ?? "", integration, temp_mapping, styling, templateType, templateTone, verboseMode)
      })
      
      setSaved(true)
      setExistingTemplate(true)
    }
    else{
      const folder = orgId ? orgId : user?.primaryEmailAddress?.emailAddress

      await executeWithRetries(async () => {
        const signedURL = await GetTemplateSignedURL(templateId, await getToken({template:"supabase"}) ?? '')
        await uploadToSupabaseSignedURL(file, signedURL)  
      })

      const applySuperOrgPermissions = (isSuperOrgTemplate && duplicate) || shouldApplySuperOrgPermissions      
      await executeWithRetries(async () => {
        await createTemplate(
          `dynamic_${templateId}`, 
          name ?? "Template", 
          await getToken({template:"supabase"}) ?? "", 
          integration, 
          temp_mapping, 
          styling, 
          templateType, 
          templateTone, 
          `dynamic_${duplicatedFromTemplateId}`,
          applySuperOrgPermissions,
          editLockedToOwner,
          verboseMode
        )
      })
      
      setTemplateOrganization(folder)
      setSaved(true)
      setExistingTemplate(true)
    }

    if (promptToReprocessSession && sessionToUpdate) {
        setShowReprocessNotesModal(true)
    }
  }

  const handleReprocessNotes = async (reprocess: boolean) => {
    if (reprocess) {
      await reprocessNotes(`${templateOrganization}/dynamic_${templateId}`)
    }
    setShowReprocessNotesModal(false)
  }

  const reprocessNotes = async (template_id: string): Promise<void> => {
    try {
        if (sessionToUpdate) {
            const token = await getToken({template:"supabase"}) ?? ""
            if (nodeIdToUpdate) {
                await processDocuments({
                    token, 
                    body: {
                        session_id: sessionToUpdate.id, 
                        process_type: "reprocess",
                        process_command: "process_spec",
                        specifications: [
                            createDocumentSpecification({ node_id: nodeIdToUpdate, template_id })
                        ],
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    }
                });
            } else {
                await generateSoapNotes(sessionToUpdate.id, template_id, false, token, sessionToUpdate.name)
            }

            navigate(`/history?session_id=${sessionToUpdate.id}&tab=notes`)    
        }
    } catch {
        return
    }
  }

  const updateVerboseMode = (verbose: boolean) => {
    setVerboseMode(verbose);
  }


  const setDefaultTemplate = async (template_id: string, type: string): Promise<void> => {
    if (type === TemplateType.MEDICAL.toString()) {
      await postDefaultTemplate(template_id, await getToken({template:"supabase"}) ?? "")   
    } else if (type === TemplateType.COMMUNICATIONS.toString()) {
      await postDefaultDischargeTemplate(template_id, await getToken({template:"supabase"}) ?? "")
    } else if (type === TemplateType.RECORDSRECAP.toString()) {
      await postDefaultRecordsRecapTemplate(template_id, await getToken({template:"supabase"}) ?? "")
    }
  }

  const validateTemplate = (): [string, boolean] => {
    // check that there is a template name
    if (name === undefined) {
      return ["Please provide a template name", false];
    }
    if (items.length === 0) {
      return ["Please add a section to the template", false];
    }
    // check that there are section names
    for (const item of items) {
      if (item.name === undefined) {
        return ["Please provide a name for all sections", false];
      } else {
        if([SectionTypes.NORMAL, SectionTypes.LIST, SectionTypes.COLLECTION, SectionTypes.TIMELINE].includes(item.type)) {
          if (item.entries.length === 0) {
            return ["Please add an entry to all sections", false];
          }
          for (const entry of item.entries) {
            if (entry.name === undefined) {
              return ["Please provide a name for all entries", false];
            }
            const normalizedName = normalizeSectionOrEntryName(entry.name);
            if (item.type === SectionTypes.TIMELINE && normalizedName === "date") {
              return [`Timeline section has 'date' built in. Please remove or rename ${entry.name} from the section.`, false];
            }
            if(entry.type === ContentType.HARDCODED){
              if(entry.hardcodedValue === undefined){
                return ["Please ensure all fixed text entries have a value.", false];
              }
            }
            if(entry.type === ContentType.SUBSECTION){
              if(entry.subEntries && entry.subEntries.length > 0){
                for (const subEntry of entry.subEntries) {
                  if (subEntry.name === undefined) {
                    return ["Please provide a name for all sub-entries", false];
                  }
                }
              }
              else{
                return ["Please add an entry into all sub-sections", false];
              }
            }
          }
        }
        else if(item.type === SectionTypes.SIMPLE_LIST || item.type === SectionTypes.NUMBER || item.type === SectionTypes.PARAGRAPH){
          // Should we add validation for instructions?
        }
        else if (item.type === SectionTypes.HARDCODED){
          if(item.hardcodedValue === undefined){
            return ["Please ensure all fixed text sections have a value.", false];
          }
        }
      }
    }

    // Check that there is at most one Diagram section
    let diagramSections = items.filter(item => item.type === SectionTypes.DIAGRAM);
    if (diagramSections.length > 1) {
      return ["Please ensure there is only one chart in the template", false];
    }

    return checkForDuplicateNames(items)
  };

  const overrideTemplate = (array_sections: { [key: string]: any }[]) : void => {
    let new_items:SectionType[] = []
    if(array_sections.length > 0){
      array_sections.forEach((section) => {
        let new_entries:EntryType[] = []
        if(section['entries'] && Array.isArray(section['entries']) && section['entries'].length > 0){
          section['entries'].forEach((entry) => {
            let new_sub_entries:EntryType[] = []
            if(entry['subEntries'] && Array.isArray(entry['subEntries']) && entry['subEntries'].length > 0){
              entry['subEntries'].forEach((subEntry) => {
                new_sub_entries.push({
                  entryKey: uuidv4(),
                  name:subEntry['name'],
                  description: subEntry['description'],
                  defaultValue: subEntry['default_value'],
                  type: ContentType[subEntry['type'] as keyof typeof ContentType]
                })
              })
            }
            new_entries.push({
              entryKey: uuidv4(),
              name:entry['name'],
              description: entry['description'],
              defaultValue: entry['default_value'],
              type: ContentType[entry['type'] as keyof typeof ContentType],
              subEntries: new_sub_entries
            })
          })
        }
        new_items.push({
          sectionKey:uuidv4(),
          name: section['name'],
          type: SectionTypes[section['type'] as keyof typeof SectionTypes],
          description: section['description'],
          entries: new_entries
        })
      })
    }
    setItems(new_items)
  }

  const updateEzyvetSectionName = (key: string, newName: string) => {
    const updatedMapping = { ...mapping };
    if (updatedMapping[key]) {
      updatedMapping[key].ezyvet_section_name = newName;
      setMapping(updatedMapping);
    }
  };

  const updateEzyVetSectionCategory = (key: string, newCategory: EzyVetCategories) => {
      const updatedMapping = { ...mapping };
      if (updatedMapping[key]) {
        updatedMapping[key].category = newCategory;
        setMapping(updatedMapping);
      }
  };

  const updateEzyVetVitalCategory = (sectionKey: string, entryKey:string, newCategory: EzyVetVitalsCategories) => {
    const updatedMapping = { ...mapping };
    if (updatedMapping[sectionKey]) {
      if(updatedMapping[sectionKey].inputs[entryKey]){
        updatedMapping[sectionKey].inputs[entryKey].inputName = newCategory;
      }
      else{
        updatedMapping[sectionKey].inputs[entryKey] = {
          name: entryKey,
          inputName: newCategory
        }
      }
      setMapping(updatedMapping);
    }
  };

  const updateVetspireType = (type:VetspireTypes, id?:string) => {
    UserAction(user?.primaryEmailAddress?.emailAddress ?? "", `VetspireIntegrationType_${type}`)
    if(disableExportConfiguration){
      return
    }
    setVetspireMapping(prevMapping => {
      prevMapping['type'] = type;
      prevMapping['id'] = id;
      return { ...prevMapping };
    });
  }

  const updateIntegration = (integration:string) => {
    UserAction(user?.primaryEmailAddress?.emailAddress ?? "", `TemplateIntegration_${integration}`)
    setIntegration(integration)
  }
  
  function getTemplateMapping() {
    if (integration === IntegrationType.EZYVET) {
      return saveEzyVetMapping(mapping, items);
    }
    else if (integration === IntegrationType.INSTINCT) {
      return saveInstinctMapping(mapping, items);
    }
    else if (integration === IntegrationType.VETSPIRE || pims.includes("vetspire")) {
      return vetspireMapping;
    }
    else if (integration === IntegrationType.NOT_MAPPED) {
      return undefined;
    }
  }

  const saveMapping = async () => {
    UserAction(user?.primaryEmailAddress?.emailAddress ?? "", "TemplateMapping_Saved")
    if(saved){
      const temp_mapping = getTemplateMapping();
      await updateTemplate(`dynamic_${templateId}`, name ?? "Template", await getToken({template:"supabase"}) ?? "", integration, temp_mapping, styling, templateType, templateTone)
    }
  }

  const updateStyling = (styling:TemplateStyling) => {
    setStyling(styling)
  }

  const saveStyling =  async(styling:TemplateStyling) => {
    UserAction(user?.primaryEmailAddress?.emailAddress ?? "", "TemplateStyling_Saved")
    setStyling(styling)
    if(saved){
      const temp_mapping = getTemplateMapping();
      await updateTemplate(`dynamic_${templateId}`, name ?? "Template", await getToken({template:"supabase"}) ?? "", integration, temp_mapping, styling, templateType, templateTone)
    }
  }

  const hasPermission = (...permissions: TemplatePermissions[]) => {
    if(!isSuperOrgTemplate && !shouldApplySuperOrgPermissions) {
      // If not super org template...
      if (isTemplateOwner || !editLockedToOwner || !existingTemplate) {
        if (!existingTemplate && !templateCreateEnabledPermission) {
          // If a new template and no create permission, deny everything
          return false;
        }

        return true;
      } else {
        // Else, only limited permissions allowed
        const nonOwnerTemplatePermissions = [TemplatePermissions.DUPLICATE]
        return permissions.every(permission => nonOwnerTemplatePermissions.includes(permission));
      }
    }
    else if (isSuperOrgTemplate && !duplicate){
      // If super org template and not duplicate then use has no permissions
      return false;
    }
    // If super org template and duplicate, then only editing permissions provided
    return permissions.every(permission => superOrgTemplatePermissions.includes(permission));
  }

  const { search, state } = useLocation();

  useEffect(() => {
    const getTemplateFromParam = async () => {
      const queryParams = new URLSearchParams(search);
      const template_id_temp = queryParams.get('templateId');
      const share_access_key = queryParams.get('share_access_key');
      const duplicate = queryParams.get('duplicate') === "true" ? true : false
      setDuplicate(duplicate)
      let template_object 
      let owner_VetRec

      setPromptToReprocessSession(queryParams.get('reprocess_session') === 'true');

        // Check for template overwrite in state
        if (state?.template) {
          setTemplateLoading(true);
          try {
            overrideTemplate(state.template)
            setTemplateType(TemplateType.MEDICAL);
          } catch (error) {
            console.error('error overriding template: ', error)
          } finally {
            setTemplateLoading(false);
          }
        } else if (template_id_temp && !share_access_key) {
        setTemplateLoading(true)
        template_object = await fetchTempalteById(`dynamic_${template_id_temp}`, await getToken({template:"supabase"}) ?? "")
        owner_VetRec = isVetRecOwner(template_object['template']['owner'])
        const super_org_list:string[] = template_object['template']['super_org'] ?? []
        const doIOwnIt = (template_object['template']['owner'] === user?.primaryEmailAddress?.emailAddress)
        const doIOrTeamOwnIt = (template_object['template']['owner'] === user?.primaryEmailAddress?.emailAddress || template_object['template']['organization'] === orgId)
        const isCurrentSuperOrgTemplate = super_org_list.length > 0 && super_org_list.includes(superOrg)
        setIsSuperOrgTemplate(isCurrentSuperOrgTemplate && !doIOrTeamOwnIt && !superOrgOwner) // If I own it then treat normally, not a super org
        setIsTemplateOwner(doIOwnIt)
        setTemplateOwner(template_object['template']['owner'])

        const duplicationDetails = template_object['template']['duplication_details']
        if (duplicationDetails) {
          // Apply if flag is set on template and not a super org owner
          setShouldApplySuperOrgPermissions(!superOrgOwner && duplicationDetails["apply_super_org_permissions"])
        }

        const duplicateOrVetRecOwned = duplicate || owner_VetRec 
        setTemplateId(duplicateOrVetRecOwned ? uuidv4() : template_id_temp)
        setDuplicatedFromTemplateId(duplicateOrVetRecOwned ? template_id_temp : undefined)
        setExistingTemplate(!duplicateOrVetRecOwned)
        setIsPersonalTemplate(inOrganization && !duplicateOrVetRecOwned && doIOwnIt && template_object['template']['organization'] == user?.primaryEmailAddress?.emailAddress)

        const template = await GetTemplateDefinition(`dynamic_${template_id_temp}`, await getToken({template:"supabase"}) ?? '')
        setItems(JSON.parse(template) as SectionType[])
        setName(duplicateOrVetRecOwned ? `${template_object['template']['name']} Copy` : template_object['template']['name'])
        setVerboseMode((template_object['template']['verbose_mode'] ?? false) as boolean)
        setTemplateType(template_object['template']['type'] ?? TemplateType.MEDICAL)
        setTemplateLoading(false)
        setIntegration(template_object['template']['integration'])
        setTemplateTone(template_object['template']['tone'] ?? communicationTemplateTones[1].value)
        if(template_object['template']['mapping']) {
          let mapping = {}
          if(template_object['template']['integration'] === IntegrationType.EZYVET){
            mapping = ezyVetMappingToObject(template_object['template']['mapping'])
          }
          else if(template_object['template']['integration'] === IntegrationType.INSTINCT){
            mapping = instinctMappingToObject(template_object['template']['mapping'])
          }
          else if(template_object['template']['integration'] === IntegrationType.VETSPIRE){
            if(template_object['template']['mapping']['type']){
              setVetspireMapping(template_object['template']['mapping'])
            }
            else{
              setVetspireMapping({type: defaultVetspireBehavior })
            }
          }
          setMapping(mapping)
        }

        setEditLockedToOwner(duplicateOrVetRecOwned ? false : (template_object['template']['edit_locked_to_owner'] ?? false))
      }
      else if(template_id_temp && share_access_key) {
        setTemplateLoading(true)
        template_object = await fetchTempalteById(`dynamic_${template_id_temp}`, await getToken({template:"supabase"}) ?? "", share_access_key)
        owner_VetRec = false
        setTemplateId(uuidv4())
        setExistingTemplate(false)
        const template = await GetTemplateDefinition(`dynamic_${template_id_temp}`, await getToken({template:"supabase"}) ?? '', share_access_key)
        setItems(JSON.parse(template ?? "") as SectionType[])
        setName(template_object['template']['name'])
        setVerboseMode((template_object['template']['verbose_mode'] ?? false) as boolean)
        setTemplateType(template_object['template']['type'] ?? TemplateType.MEDICAL)
        setTemplateLoading(false)
        setIntegration(template_object['template']['integration'])
        setTemplateTone(template_object['template']['tone'] ?? communicationTemplateTones[1].value)
        if(template_object['template']['mapping']) {
          let mapping = {}
          if(template_object['template']['integration'] === IntegrationType.EZYVET){
            mapping = ezyVetMappingToObject(template_object['template']['mapping'])
          }
          else if(template_object['template']['integration'] === IntegrationType.INSTINCT){
            mapping = instinctMappingToObject(template_object['template']['mapping'])
          }
          else if(template_object['template']['integration'] === IntegrationType.VETSPIRE){
            if(template_object['template']['mapping']['type']){
              setVetspireMapping(duplicate && disableExportConfiguration ? {type: defaultVetspireBehavior} : template_object['template']['mapping'])
            }
            else{
              setVetspireMapping({type: defaultVetspireBehavior })
            }
          }
          setMapping(mapping)
        }
      }

      if(template_object && template_object['template']['styling']){
        const styling_temp: TemplateStyling = {
          title_bold: template_object.template.styling.title_bold === undefined ? true : template_object.template.styling.title_bold,
          abnormals_bolded: template_object.template.styling.abnormals_bolded === undefined ? false : template_object.template.styling.abnormals_bolded,
          abnormals_color: template_object.template.styling.abnormals_color === undefined ? false : template_object.template.styling.abnormals_color,
          abnormals_uppercase: template_object.template.styling.abnormals_uppercase === undefined ? false : template_object.template.styling.abnormals_uppercase,
          show_not_mentioned: template_object.template.styling.show_not_mentioned === undefined ? true : template_object.template.styling.show_not_mentioned,
          bullet_points: template_object.template.styling.bullet_points === undefined ? true : template_object.template.styling.bullet_points,
          list_bullet_points: template_object.template.styling.list_bullet_points === undefined ? true : template_object.template.styling.list_bullet_points,
          title_italic: template_object.template.styling.title_italic === undefined ? false : template_object.template.styling.title_italic
        }
        if(template_object?.template?.styling?.granular_styling){
          styling_temp.granular_styling = template_object.template.styling.granular_styling
        }
        setStyling(styling_temp)
        setTemplateOrganization(duplicate || owner_VetRec || share_access_key ?  (orgId ? orgId : user?.primaryEmailAddress?.emailAddress) : template_object['template']['organization'])
        setSaved(duplicate || owner_VetRec || share_access_key ? false : true)
      }
    }
    getTemplateFromParam()
  }, [search, state]);

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const reprocess_session = queryParams.get('reprocess_session') === 'true';
    const session_id = queryParams.get('session_id');
    const node_id = queryParams.get('node_id');

    if (session_id && reprocess_session && !sessionToUpdate) {
      const fetchSession = async () => {
        const token = await getToken({ template:"supabase" }) ?? "";
        const session = await fetchSessionById(session_id, token);
        setSessionToUpdate(session);
      }
      fetchSession().catch(() => {});
      setNodeIdToUpdate(node_id ?? undefined)
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line prefer-const
    let sections:{[key:string]:EzyVetSection}  = {}
    // TODO: Look at auto-mapping
    items.forEach((section) => {
        const entryNames = section.entries?.map((entry) => entry.name?.toLowerCase()).filter((name) => name !== undefined)        
        if(![SectionTypes.GENERAL_INSTRUCTIONS, SectionTypes.DIAGRAM].includes(section.type)){
          sections[section.name?.toLowerCase() ?? ""] = {
            name: mapping[section.name?.toLowerCase() ?? ""] ? mapping[section.name?.toLowerCase() ?? ""].name : "",
            ezyvet_section_name: mapping[section.name?.toLowerCase() ?? ""] ? mapping[section.name?.toLowerCase() ?? ""].ezyvet_section_name : "",
            category: mapping[section.name?.toLowerCase() ?? ""] ? mapping[section.name?.toLowerCase() ?? ""].category : naiveMappingAttempt(section.name?.toLowerCase() ?? ""),
            inputs: mapping[section.name?.toLowerCase() ?? ""] ? mapping[section.name?.toLowerCase() ?? ""].inputs : naiveVitalsMappingAttempt(entryNames)
          }
        }
    })
    setMapping(sections)
  }, [items])

  const dismissUndo = () => {
    setUndoMessage(undefined)
    setLastDeletedItem(undefined)
  }

  const handleUndo = () => {
    if (!lastDeletedItem) return;
  
    if (lastDeletedItem.type === 'section' && lastDeletedItem.section) {
      setItems(currentItems => {
        const newItems = [...currentItems];
        const insertIndex = lastDeletedItem.index !== undefined ? lastDeletedItem.index : newItems.length;
        newItems.splice(insertIndex, 0, lastDeletedItem.section!);
        return newItems;
      });
    } else if (lastDeletedItem.type === 'entry' && lastDeletedItem.entry) {
      setItems(currentItems => 
        currentItems.map(section => {
          if (section.sectionKey !== lastDeletedItem.sectionKey) return section;
  
          if (!lastDeletedItem.parentEntryKey) {
            // Restore main entry
            const entries = [...section.entries];
            const insertIndex = lastDeletedItem.index !== undefined ? lastDeletedItem.index : entries.length;
            entries.splice(insertIndex, 0, lastDeletedItem.entry!);
            return { ...section, entries };
          } else {
            // Restore sub-entry
            return {
              ...section,
              entries: section.entries.map(entry => {
                if (entry.entryKey !== lastDeletedItem.parentEntryKey) return entry;
                const subEntries = entry.subEntries ? [...entry.subEntries] : [];
                const insertIndex = lastDeletedItem.index !== undefined ? lastDeletedItem.index : subEntries.length;
                subEntries.splice(insertIndex, 0, lastDeletedItem.entry!);
                return { ...entry, subEntries };
              })
            };
          }
        })
      );
    }
    
    setLastDeletedItem(undefined);
    setUndoMessage(undefined);
  };

  const value = {
    setDefaultTemplate,
    name,
    items,
    mapping,
    integration,
    styling,
    entryHoverIndex,
    sectionHoverIndex,
    subSectionHover,
    subEntryHoverIndex,
    sectionDragged,
    entryDragged,
    templateType,
    templateTone,
    templateLoading,
    templateId,
    duplicatedFromTemplateId,
    vetspireMapping,
    existingTemplate,
    showReprocessNotesModal,
    sessionToUpdate,
    isSuperOrgTemplate,
    shouldApplySuperOrgPermissions,
    duplicate,
    editLockedToOwnerLoading,
    editLockedToOwner,
    isTemplateOwner,
    templateOwner,
    isPersonalTemplate,
    templateCreateEnabledPermission,
    templateEditEnabledPermission,
    onEditLockedToOwnerChanged,
    updateIntegration,
    addEntryToItem,
    updateSectionName,
    updateSectionEmptyName,
    updateSectionType,
    updateSectionDescription,
    updateSectionDefaultValue,
    updateSectionHardcodedValue,
    updateSectionEmptyHardcodedValue,
    addSection,
    updateName,
    deleteSection,
    updateEntryName,
    updateEntryEmptyName,
    updateEntryDescription,
    updateEntryHardcodedValue,
    updateEntryEmptyHardcodedValue,
    updateEntryDefaultValue,
    updateEntryType,
    deleteEntry,
    swapSectionsByKeys,
    swapEntries,
    swapSubEntries,
    saveTemplate,
    validateTemplate,
    overrideTemplate,
    updateEzyvetSectionName,
    updateEzyVetSectionCategory,
    updateEzyVetVitalCategory,
    updateVetspireType,
    saveMapping,
    saveStyling,
    updateStyling,
    handleSectionOver,
    handleEntryOver,
    handleSubSectionOver,
    handleSubEntryOver,
    handleSectionDragged,
    handleEntryDragged,
    updateTemplateType,
    handleTemplateTone,
    handleReprocessNotes,
    hasPermission,
    undoMessage,
    dismissUndo,
    handleUndo,
    updateVerboseMode,
    verboseMode,
    isTemplateDefaultedInClerk
  }

  return <TemplateContext.Provider value={value}>{children}</TemplateContext.Provider>;
};

